import axios from 'axios';

import { createApiClient } from '@/api/http-client';
import { handleSessionExpired } from '@/api/session-expiry';
import { config } from '@/config';
import { createLogger } from '@/utils/logging';

const logger = createLogger('PortfolioAPI', 'api');

/**
 * NOTE (Spec 0099 / EPIC-05): `fetchPortfolioProjects` calls the real
 * `GET /api/v1/portfolio/projects` endpoint (back-end/src/router/portfolio.py),
 * which returns the whole portfolio (merged Snowflake + Postgres health data)
 * unfiltered and unpaginated. Filtering, sorting, pagination, stat-card counts,
 * and filter-dropdown options are all derived client-side from that one fetch
 * (see `usePortfolio` and `portfolio_table.utils.ts`) — there is no server-side
 * filtering/pagination and no dedicated `/filters` or `/export` endpoint.
 *
 * Health fields (`on_schedule`, `csat`, `team_health`, `last_weekly_update_at`)
 * resolve to `pending`/`null` for any project with no `project_weekly_updates`
 * row yet — spec 0101 (Weekly Update Form) is the intended producer of those
 * rows; this is expected, not an error.
 */

/** Typed error thrown by portfolio API functions. */
export class PortfolioApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PortfolioApiError';
  }
}

/** R/Y/G health state; `pending` = no weekly update ever filed (grey). */
export type HealthStatus = 'green' | 'yellow' | 'red' | 'pending';

/** Stat-card presets (also the client-side status filter). */
export type PortfolioStatusPreset = 'on_track' | 'watching' | 'at_risk' | 'missing_update';

/**
 * SOW lifecycle state. `active`/`inactive` are computed server-side from OpenAir
 * PROJECT_RAW start/end dates (today within range = active); the rest are reserved
 * for a future real SOW source and never sent by the current back end.
 */
export type SowStatus = 'active' | 'inactive' | 'draft' | 'pending' | 'expired' | 'closed';

/** Sortable columns. */
export type PortfolioSortField =
  'client_name' | 'project_name' | 'overall_status' | 'start_date' | 'end_date';

export type SortDirection = 'asc' | 'desc';

/** A single row in the portfolio project table. */
export interface PortfolioProjectRow {
  id: string;
  client_id: string;
  client_name: string;
  project_name: string;
  snowflake_project_id: string | null;
  sync_source: string;
  /** Computed server-side from OpenAir start/end dates; `null` when either date is missing. */
  sow_status: SowStatus | null;
  overall_status: HealthStatus;
  on_schedule: HealthStatus;
  on_budget: HealthStatus;
  csat: HealthStatus;
  team_health: HealthStatus;
  offering: string;
  owner_name: string;
  delivery_lead_name: string;
  pm_name: string;
  /** Count of additional PMs beyond `pm_name` (renders as the "+N" chip). */
  pm_extra_count?: number;
  start_date: string;
  end_date: string;
  timeline_pct: number;
  last_weekly_update_at: string | null;
  missing_update_flag: boolean;
  /** Only present for Director-or-higher users; absent (not null) for others. */
  ode_budget?: number | null;
  /** Only present for Director-or-higher users; absent (not null) for others. */
  ode_margin?: number | null;
}

/** Portfolio-wide health rollup counts (excludes archived). */
export interface PortfolioStats {
  on_track: number;
  watching: number;
  at_risk: number;
  missing_update: number;
}

/** A single RYG health-indicator filter (`green`/`yellow`/`red`); `undefined` = All. */
export type HealthFilter = Exclude<HealthStatus, 'pending'>;

/** Client-side filter/sort state consumed by `portfolio_table.utils.ts`. */
export interface PortfolioQueryParams {
  client_id?: string[];
  /** Project identifiers selected in the Project scope dropdown. */
  project_id?: string[];
  project_name?: string;
  offering?: string[];
  /** Delivery-lead display names, matched exactly (`delivery_lead_name` on the real API). */
  leader?: string[];
  /** `'all'` omits the filter. */
  sow_status?: SowStatus | 'all';
  /** Health-indicator filters (More filters → Health). Omit for "All". */
  overall_status?: HealthFilter;
  on_schedule?: HealthFilter;
  csat?: HealthFilter;
  team_health?: HealthFilter;
  starts_on_before?: string | null;
  ends_on_after?: string | null;
  status?: PortfolioStatusPreset | null;
  page?: number;
  per_page?: number;
  sort_by?: PortfolioSortField;
  sort_dir?: SortDirection;
}

/** Option lists for the filter controls (derived from the full loaded dataset). */
export interface PortfolioFilterOptions {
  clients: { value: string; label: string }[];
  projects: { value: string; label: string }[];
  offerings: string[];
  leaders: string[];
  sowStatuses: SowStatus[];
}

const portfolioApiClient = createApiClient({
  baseURL: config.apiUrl,
  timeout: 90000,
  headers: { 'Content-Type': 'application/json' },
});

/** Raw shape of one row from `GET /api/v1/portfolio/projects` (`PortfolioProjectItem` in the back end). */
interface PortfolioApiProjectItem {
  id: string;
  client_id: string | null;
  client_name: string;
  project_name: string;
  snowflake_project_id: string;
  sow_status: string | null;
  overall_status: HealthStatus;
  on_schedule: HealthStatus;
  on_budget: HealthStatus;
  csat: HealthStatus;
  team_health: HealthStatus;
  offering: string | null;
  owner_name: string | null;
  delivery_lead_name: string | null;
  pm_name: string | null;
  pm_extra_count: number;
  start_date: string | null;
  end_date: string | null;
  last_weekly_update_at: string | null;
  missing_update_flag: boolean;
}

interface PortfolioApiResponseDTO {
  projects: PortfolioApiProjectItem[];
}

const computeTimelinePct = (startDate: string | null, endDate: string | null): number => {
  const start = startDate ? Date.parse(startDate) : Number.NaN;
  const end = endDate ? Date.parse(endDate) : Number.NaN;
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return 0;
  }
  const ratio = (Date.now() - start) / (end - start);
  return Math.round(Math.min(1, Math.max(0, ratio)) * 100);
};

/**
 * Maps one real API row to `PortfolioProjectRow`. Absorbs two shape gaps
 * between the real endpoint and the FE's DTO (both intentional per
 * Portfolio Dashboard.md, not bugs to fix here):
 *  - `sync_source`/`timeline_pct` don't exist on the real row (every row is
 *    Snowflake-sourced; timeline is derived client-side from the dates).
 *  - `ode_budget`/`ode_margin` are not selected by the real query yet
 *    ("No financial fields in this pass") — left `undefined`, not null-faked.
 * Nullable display fields (offering/owner/leader/PM/client_id) default to
 * `''` since the FE type models them as required strings and nothing
 * downstream needs to distinguish "empty" from "not sourced" for these.
 */
const mapApiProjectToRow = (item: PortfolioApiProjectItem): PortfolioProjectRow => ({
  id: item.id,
  client_id: item.client_id ?? '',
  client_name: item.client_name,
  project_name: item.project_name,
  snowflake_project_id: item.snowflake_project_id,
  sync_source: 'snowflake',
  sow_status: item.sow_status as SowStatus | null,
  overall_status: item.overall_status,
  on_schedule: item.on_schedule,
  on_budget: item.on_budget,
  csat: item.csat,
  team_health: item.team_health,
  offering: item.offering ?? '',
  owner_name: item.owner_name ?? '',
  delivery_lead_name: item.delivery_lead_name ?? '',
  pm_name: item.pm_name ?? '',
  pm_extra_count: item.pm_extra_count,
  start_date: item.start_date ?? '',
  end_date: item.end_date ?? '',
  timeline_pct: computeTimelinePct(item.start_date, item.end_date),
  last_weekly_update_at: item.last_weekly_update_at,
  missing_update_flag: item.missing_update_flag,
});

const parsePortfolioApiError = (error: unknown): PortfolioApiError => {
  handleSessionExpired(error);
  if (!axios.isAxiosError(error)) {
    const message = error instanceof Error ? error.message : 'Request failed';
    return new PortfolioApiError(message);
  }
  const data = error.response?.data as Record<string, unknown> | undefined;
  const detail = data?.detail;
  if (typeof detail === 'string' && detail.trim().length > 0) {
    return new PortfolioApiError(detail.trim());
  }
  return new PortfolioApiError(error.message);
};

/**
 * Fetches the whole portfolio (merged Snowflake + Postgres health data) in one
 * call, unfiltered and unpaginated. `usePortfolio` calls this once per session
 * and derives filtering/sorting/pagination/stats/dropdown-options from the
 * result client-side. GET /portfolio/projects
 */
export const fetchPortfolioProjects = async (
  accessToken: string,
): Promise<PortfolioProjectRow[]> => {
  try {
    const response = await portfolioApiClient.get<PortfolioApiResponseDTO>('/portfolio/projects', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data.projects.map(mapApiProjectToRow);
  } catch (error) {
    const parsedError = parsePortfolioApiError(error);
    logger.error('Failed to fetch portfolio projects', parsedError);
    throw parsedError;
  }
};

/** Client id → name and project id → name maps, derived from already-fetched portfolio rows. */
export interface PortfolioIdNameMaps {
  clientNameById: Record<string, string>;
  projectNameById: Record<string, string>;
}

/**
 * Builds id→name lookup maps from portfolio rows, for breadcrumb/label resolution elsewhere in the
 * app. Used by both `PortfolioDashboardPage` and `ClientProjectsPage`, which each independently
 * fetch this same row data.
 */
export const buildIdNameMaps = (rows: PortfolioProjectRow[]): PortfolioIdNameMaps => {
  const clientNameById: Record<string, string> = {};
  const projectNameById: Record<string, string> = {};
  for (const row of rows) {
    if (row.client_id && row.client_name) {
      clientNameById[row.client_id] = row.client_name;
    }
    if (row.id && row.project_name) {
      projectNameById[row.id] = row.project_name;
    }
  }
  return { clientNameById, projectNameById };
};

/** Fixed per product decision (not derived from data) — see PortfolioFilters' SOW dropdown. */
const SOW_STATUS_FILTER_OPTIONS: SowStatus[] = ['active', 'inactive'];

const toSortedOptionList = (values: Set<string>): { value: string; label: string }[] =>
  [...values].toSorted((a, b) => a.localeCompare(b)).map((value) => ({ value, label: value }));

/**
 * Derives Client/Project/Offering/Leader dropdown options from live rows.
 * `value === label` for clients/projects: the real API filters by exact
 * `client_name`/`project_name` string, not a separate stable id, so the
 * option value must be the display name itself.
 *
 * `selectedClientNames` cascades the Project options: when non-empty, only
 * projects belonging to those clients are offered (issue #203).
 */
export const buildFilterOptionsFromRows = (
  rows: PortfolioProjectRow[],
  selectedClientNames: string[] = [],
): PortfolioFilterOptions => {
  const clientScope = selectedClientNames.length > 0 ? new Set(selectedClientNames) : null;
  const clientNames = new Set<string>();
  const projectNames = new Set<string>();
  const offerings = new Set<string>();
  const leaders = new Set<string>();
  for (const row of rows) {
    if (row.client_name) {
      clientNames.add(row.client_name);
    }
    if (row.project_name && (!clientScope || clientScope.has(row.client_name))) {
      projectNames.add(row.project_name);
    }
    if (row.offering) {
      offerings.add(row.offering);
    }
    if (row.delivery_lead_name) {
      leaders.add(row.delivery_lead_name);
    }
  }
  return {
    clients: toSortedOptionList(clientNames),
    projects: toSortedOptionList(projectNames),
    offerings: [...offerings].toSorted((a, b) => a.localeCompare(b)),
    leaders: [...leaders].toSorted((a, b) => a.localeCompare(b)),
    sowStatuses: SOW_STATUS_FILTER_OPTIONS,
  };
};

const CSV_BASE_HEADERS = [
  'Client',
  'Project',
  'Project ID',
  'SOW Status',
  'Overall Status',
  'On Schedule',
  'CSAT',
  'Team Health',
  'Offering',
  'Owner',
  'Delivery Lead',
  'PM',
  'Start',
  'End',
  'Missing Update',
];
const CSV_FINANCIAL_HEADERS = ['ODE Budget', 'ODE Margin'];

const escapeCsv = (value: string | number | boolean | null | undefined): string => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
};

/** Builds CSV text from already-loaded portfolio rows (director-gated financial columns). */
export const rowsToCsv = (rows: PortfolioProjectRow[], isDirectorOrHigher: boolean): string => {
  const headers = isDirectorOrHigher
    ? [...CSV_BASE_HEADERS, ...CSV_FINANCIAL_HEADERS]
    : CSV_BASE_HEADERS;
  const lines = [headers.join(',')];
  for (const row of rows) {
    const cells: (string | number | boolean | null | undefined)[] = [
      row.client_name,
      row.project_name,
      row.id,
      row.sow_status,
      row.overall_status,
      row.on_schedule,
      row.csat,
      row.team_health,
      row.offering,
      row.owner_name,
      row.delivery_lead_name,
      row.pm_extra_count ? `${row.pm_name} (+${row.pm_extra_count})` : row.pm_name,
      row.start_date,
      row.end_date,
      row.missing_update_flag,
    ];
    if (isDirectorOrHigher) {
      cells.push(row.ode_budget ?? '', row.ode_margin ?? '');
    }
    lines.push(cells.map(escapeCsv).join(','));
  }
  return lines.join('\n');
};
